import {
  Divider,
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  useDisclosure,
  Image,
} from '@chakra-ui/react'
import { Button, Chip, Icon, Label } from '@ubirider/pick-component-library'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import Invoice from './components/Invoice/Invoice'
import { userTimezone } from '../../../../../../App'
import { useCards } from '../../../../../../context/DataProvider'
import React from 'react'
import Validation from './components/Validation/Validation'
import { SettleAmountModal } from '../../../../../../modals/SettleAmountModal'
import { NoCardsModal } from '../../../../../../modals/NoCardsModal'

import EditIcon from '../../../../../../assets/edit_purple.svg'
import VisaIcon from '../../../../../../assets/visa.svg'
import MastercardIcon from '../../../../../../assets/mastercard_transparent.svg'
import AmericanExpressIcon from '../../../../../../assets/amex.svg'
import PlaceholderCardIcon from '../../../../../../assets/credit_card.svg'
import CashIcon from '../../../../../../assets/cash.svg'
import DownloadIcon from '../../../../../../assets/download.svg'
import ChevronDownIcon from '../../../../../../assets/arrow_down.svg'

const CARD_BRANDS = [
  { regex: /^4/, icon: VisaIcon },
  { regex: /^5[1-5]/, icon: MastercardIcon },
  { regex: /^(34|37)/, icon: AmericanExpressIcon },
  { regex: /^6(?:2|011|5)/, icon: PlaceholderCardIcon },
]

const formatCurrency = val =>
  'R$ ' + new Intl.NumberFormat('pt-PT', { style: 'decimal', minimumFractionDigits: 2 }).format(val ?? 0)

const getCardIcon = pan => {
  const brand = CARD_BRANDS.find(b => b.regex.test(pan))
  return brand ? brand.icon : PlaceholderCardIcon
}

export function Trip({ trip, onEdit, isLast }) {
  const { t } = useTranslation()

  const { isOpen: isOpenNoCards, onOpen: onOpenNoCards, onClose: onCloseNoCards } = useDisclosure()
  const { onOpen: onOpenSettleAccount, isOpen: isOpenSettleAccount, onClose: onCloseSettleAccount } = useDisclosure()

  const { cards } = useCards()

  const isRecent = Math.abs(Date.now() - Number(trip?.date ?? 0)) <= 15000
  const isCardPayment = trip.paymentMethod === 'card'

  const cardPAN = isCardPayment
    ? (trip.cardNumber ?? cards.find(c => c.cardUniqueReference === trip.cardUniqueReference)?.number)
    : null

  const getContainerStyles = () => {
    return {
      bg: isRecent ? 'secondary.100' : 'neutral.25',
      shadow: '0px 0px 20px rgba(0, 0, 0, 0.05)',
      border: '1px solid #EAECF0',
      borderBottomRadius: isLast ? '8px' : '0px',
      borderBottom: isLast ? '1px solid #D0D5DD' : 'none',
    }
  }

  return (
    <Accordion allowToggle>
      <AccordionItem border='none'>
        {({ isExpanded }) => (
          <Box {...getContainerStyles(isExpanded)} transition='all 0.2s'>
            <AccordionButton py='16px' _hover={{ borderColor: 'secondary.500' }}>
              <Flex flex='1' align='center' gap='4px' pr={{ sm: '8px', md: '16px' }}>
                {/* Toggle Icon */}
                <Flex flex='1' display={{ sm: 'none', lg: 'flex' }}>
                  <Box
                    transition='0.2s'
                    transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                    display='inline-flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Icon src={ChevronDownIcon} />
                  </Box>
                </Flex>

                {/* Date */}
                <Text fontSize='13px' flex={{ sm: '3', lg: '2' }} textAlign='left'>
                  {DateTime.fromMillis(trip.date).setZone(userTimezone).toFormat('dd/MM/yyyy, HH:mm')}
                </Text>

                {/* Payment Method */}
                <Flex flex='3' gap='8px' align='center' display={{ sm: 'none', lg: 'flex' }}>
                  {trip.status === 'paid'  && <Icon src={isCardPayment ? getCardIcon(cardPAN) : CashIcon} />}
                  <Text display={{ base: 'none', md: 'block' }} fontSize='13px'>
                    {trip.status === 'paid' ? isCardPayment ? `Cartão ${cardPAN ? `••• ${cardPAN.slice(-4)}` : ''}` : t('acqua_vias.payment_method_cash_name') : '-'}
                  </Text>
                </Flex>

                {/* Vehicle */}
                <Flex flex={{ sm: '3', lg: '2' }} justify='center'>
                  {trip.vehicleLicensePlate ? (
                    <Chip label={trip.vehicleLicensePlate} />
                  ) : (
                    <Text fontSize='13px'>-</Text>
                  )}
                </Flex>

                {/* Amount */}
                <Flex flex='2' justify='center'>
                  <Text
                    fontWeight='700'
                    fontSize='13px'
                    color={{ sm: trip.status === 'paid' ? 'success.600' : 'danger.600', lg: '#1D2939' }}
                  >
                    {formatCurrency(trip.amount)}
                  </Text>
                </Flex>

                {/* Payment Status */}
                <Flex flex='2' justify='flex-end' display={{ sm: 'none', lg: 'flex' }}>
                  <Label
                    label={trip.status === 'paid' ? t('acqua_vias.paid_label') : t('acqua_vias.not_paid_label')}
                    type={trip.status === 'paid' ? 'success' : 'danger'}
                    style='outline'
                  />
                </Flex>

                <Flex flex='1' display={{ sm: 'flex', lg: 'none' }} alignItems='flex-end' justifyContent='flex-end'>
                  <Box
                    transition='0.2s'
                    transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                    display='inline-flex'
                  >
                    <Icon src={ChevronDownIcon} />
                  </Box>
                </Flex>
              </Flex>
            </AccordionButton>

            <AccordionPanel px={{ base: 4, md: '24px' }} pb='16px'>
              <Divider mb='16px' />

              {trip.status === 'not_paid' && (
                <Flex h='36px' align='center' justify='space-between' gap='24px' mb='24px'>
                  <Text fontSize='13px' fontWeight='600' color='danger.600'>
                    {t('acqua_vias.last_trips_select_payment_method_settle_amount_inline')}
                  </Text>
                  <Button
                    onClick={cards.length > 0 ? onOpenSettleAccount : onOpenNoCards}
                    label={t('acqua_vias.pay_button')}
                    size='small'
                    style='solid'
                    type='neutralDark'
                  />
                </Flex>
              )}

              {/* Desktop Details */}
              <Box mt='-8px'>
                {trip.tickets.map(ticket => (
                  <TripDetailRow t={t} ticket={ticket} />
                ))}
              </Box>

              {/* Mobile Details */}
              <Box mt='8px' display={{ base: 'flex', md: 'none' }}>
                <MobileTripInfo t={t} cardPAN={cardPAN} />
              </Box>

              {/* Tax Info & Editing */}
              <Flex
                gap={{ sm: '8px', lg: '48px' }}
                mt={{ sm: '8px', lg: '16px' }}
                direction={{ sm: 'column', lg: 'row' }}
              >
                <Flex align={{ sm: 'flex-start', lg: 'center' }} gap='8px'>
                  <Text fontSize='13px' fontWeight='700'>
                    {t('acqua_vias.last_trips_invoice_id')}
                  </Text>
                  <Text fontSize='13px'>{trip.invoices?.[0]?.invoiceName || '-'}</Text>
                  {trip.invoices?.[0]?.invoiceURL &&
                    <a href={trip.invoices?.[0]?.invoiceURL} target='_blank' rel='noreferrer'>
                      <Image src={DownloadIcon} />
                    </a>
                  }
                </Flex>
                <Flex align='center' gap='8px'>
                  <Text fontSize='13px' fontWeight='700'>
                    {t('acqua_vias.last_trips_tax_number')}
                  </Text>
                  <Text fontSize='13px'>
                    {trip.invoices?.[0]?.vatNumber
                      ?.replace(/\D/g, '')
                      ?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') || '000.000.000-00'}
                  </Text>
                  <Button
                    onClick={onEdit}
                    size='small'
                    style='typo'
                    type='secondary'
                    disabled={!trip.invoices?.[0]?.invoiceURL}
                    leftIconProps={{ src: EditIcon, size: 'small' }}
                  />
                </Flex>
              </Flex>

              {/* Validations List */}
              {trip.validations?.length > 0 &&
                <Flex direction='column' mt='16px' gap='8px' rounded='8px' p='16px' bg='neutral.50' border='1px solid #F2F4F7'>
                  <Text fontSize='13px' fontWeight='700'>
                    {t('acqua_vias.last_trips_validations_table_title')}
                  </Text>
                  <Divider />
                  <Flex w='100%' align='center' gap='8px'>
                    <Text fontSize='13px' flex='2'>
                      {t('travel_history.date')}
                    </Text>
                    <Text fontSize='13px' flex='2'>
                      {t('acqua_vias.last_trips_stops_column_header')}
                    </Text>
                    <Text fontSize='13px' flex='2' textAlign={{ sm: 'right', lg: 'left' }}>
                      {t('acqua_vias.last_trips_status_column_header')}
                    </Text>
                  </Flex>
                  {trip.validations?.map((validation, i) => (
                    <Validation key={i} validation={validation} />
                  ))}
                </Flex>
              }

              {/* Documents List */}
              <Flex direction='column' mt='24px' mb='8px' p='16px' gap='8px' rounded='8px' bg='neutral.50' border='1px solid #F2F4F7'>
                <Text fontSize='13px' fontWeight='700'>
                  {t('acqua_vias.last_trips_documents_table_title')}
                </Text>
                <Divider />
                <Flex w='100%' align='center' gap='8px'>
                  <Text fontSize='13px' flex='2'>
                    {t('travel_history.date')}
                  </Text>
                  <Text fontSize='13px' flex='3'>
                    {t('acqua_vias.last_trips_invoice_column_header')}
                  </Text>
                  <Text fontSize='13px' flex='2' textAlign={{ sm: 'right', lg: 'left' }}>
                    {t('acqua_vias.last_trips_status_column_header')}
                  </Text>
                  <Text fontSize='13px' flex='2' display={{ sm: 'none', md: 'flex' }}>
                    {t('acqua_vias.last_trips_credit_note_id_column_header')}
                  </Text>
                </Flex>
                {trip.invoices?.map((invoice, i) => (
                  <Invoice key={i} invoice={invoice} />
                ))}
              </Flex>
            </AccordionPanel>
          </Box>
        )}
      </AccordionItem>

      <NoCardsModal isOpen={isOpenNoCards} onOpen={onOpenNoCards} onClose={onCloseNoCards} />
      <SettleAmountModal
        onOpen={onOpenSettleAccount}
        onClose={onCloseSettleAccount}
        isOpen={isOpenSettleAccount}
        trips={[trip]}
        totalAmount={trip.amount}
      />
    </Accordion>
  )
}

// Internal small helpers
const TripDetailRow = ({ t, ticket }) => (
  <Flex direction={{ sm: 'column', lg: 'row' }} borderBottom='1px solid #EAECF0' gap='8px' justify='space-between' py='8px'>
    <Flex gap='8px'>
      <Text fontSize='13px'>{t('acqua_vias.last_trips_ticket')}</Text>
      <Text fontSize='13px' fontWeight='700'>
        {ticket.name}
      </Text>
    </Flex>
    <Flex gap='8px'>
      <Text fontSize='13px'>{t('acqua_vias.last_trips_discount')}</Text>
      <Text fontSize='13px' fontWeight='700'>
        {formatCurrency(ticket.discount)}
      </Text>
    </Flex>
    <Flex gap='8px'>
      <Text fontSize='13px'>{t('acqua_vias.last_trips_total')}</Text>
      <Text fontSize='13px' fontWeight='700'>
        {formatCurrency(ticket.price)}
      </Text>
    </Flex>
  </Flex>
)

const MobileTripInfo = ({ t, cardPAN }) => (
  <Flex align='center' gap='8px' mb='16px'>
    <Text fontSize='13px'>{t('acqua_vias.payment_method_column_header')}</Text>
    <Icon src={cardPAN ? getCardIcon(cardPAN) : CashIcon} />
    <Text fontSize='14px' fontWeight='700'>
      {t(cardPAN ? cardPAN.replace(/(.{4})/g, '$1 ').replaceAll('X', '•') : 'acqua_vias.payment_method_cash_name')}
    </Text>
  </Flex>
)
